/**
 * Expense status enumeration for workflow management
 * Requirements: 2.4, 3.2, 3.3, 4.2, 4.3 - Approval workflow states
 */
export enum ExpenseStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  MANAGER_APPROVED = 'Manager Approved',
  MANAGER_REJECTED = 'Manager Rejected',
  FINANCE_APPROVED = 'Finance Approved',
  FINANCE_REJECTED = 'Finance Rejected',
  REIMBURSED = 'Reimbursed'
}

/**
 * Expense category enumeration
 * Requirements: 2.1, 4.4 - Expense categorization
 */
export enum ExpenseCategory {
  TRAVEL = 'Travel',
  MEALS = 'Meals',
  ACCOMMODATION = 'Accommodation',
  TRANSPORTATION = 'Transportation',
  OFFICE_SUPPLIES = 'Office Supplies',
  SOFTWARE = 'Software',
  TRAINING = 'Training',
  MARKETING = 'Marketing',
  ENTERTAINMENT = 'Entertainment',
  OTHER = 'Other'
}

/**
 * Payment method enumeration
 * Requirements: 2.1 - Payment method tracking
 */
export enum PaymentMethod {
  PERSONAL_CARD = 'Personal Card',
  COMPANY_CARD = 'Company Card',
  CASH = 'Cash',
  BANK_TRANSFER = 'Bank Transfer'
}

/**
 * Approval status enumeration
 * Requirements: 3.4, 3.5 - Approval tracking
 */
export enum ApprovalStatus {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected'
}

/**
 * Main expense interface
 * Requirements: 2.1 - Expense data capture
 */
export interface Expense {
  id: string;
  title: string;
  category: ExpenseCategory;
  amount: number;
  date: string; // ISO date string
  description?: string;
  paymentMethod: PaymentMethod;
  status: ExpenseStatus;
  submitterId: string;
  department?: string; // Department of the submitter (format: "Department | Name")
  receipts: Receipt[];
  approvals: Approval[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Receipt interface for supporting documentation
 * Requirements: 2.2, 2.5 - Receipt upload and validation
 */
export interface Receipt {
  id: string;
  expenseId: string;
  fileName: string;
  fileUrl: string;
  fileKey?: string;
  fileType: string; // 'PDF' | 'JPG' | 'PNG'
  fileSize: number;
  uploadedAt: string;
}

/**
 * Approval interface for workflow tracking
 * Requirements: 3.4, 3.5 - Approval comments and audit trail
 */
export interface Approval {
  id: string;
  expenseId: string;
  approverId: string;
  status: ApprovalStatus;
  comments?: string;
  level: number; // 1 for Manager, 2 for Finance_Admin
  timestamp: string;
}

/**
 * Category interface for system configuration
 * Requirements: 4.4 - Category management
 */
export interface Category {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  limits?: {
    maxAmount?: number;
    requiresReceipt?: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Expense creation input interface
 * Requirements: 2.1 - Expense creation
 */
export interface CreateExpenseInput {
  title: string;
  category: ExpenseCategory;
  amount: number;
  date: string;
  description?: string;
  paymentMethod: PaymentMethod;
}

/**
 * Expense update input interface
 * Requirements: 2.3 - Draft editing
 */
export interface UpdateExpenseInput {
  title?: string;
  category?: ExpenseCategory;
  amount?: number;
  date?: string;
  description?: string;
  paymentMethod?: PaymentMethod;
}

/**
 * Receipt upload input interface
 * Requirements: 2.2 - Receipt upload
 */
export interface ReceiptUploadInput {
  expenseId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

/**
 * Approval action input interface
 * Requirements: 3.4 - Approval actions
 */
export interface ApprovalActionInput {
  expenseId: string;
  status: ApprovalStatus;
  comments?: string;
}

/**
 * Pagination connection interface
 * Requirements: 10.4 - Efficient pagination
 */
export interface ExpenseConnection {
  items: Expense[];
  nextToken?: string;
  total?: number;
}

/**
 * Pagination parameters interface
 * Requirements: 10.4 - Pagination controls
 */
export interface PaginationParams {
  limit?: number;
  nextToken?: string;
  sortDirection?: 'ASC' | 'DESC';
}

/**
 * Expense filter parameters interface
 * Requirements: 6.4 - Expense list filtering
 */
export interface ExpenseFilters {
  status?: ExpenseStatus;
  dateFrom?: string;
  dateTo?: string;
  department?: string;
  category?: ExpenseCategory;
}